/* components/patterns/Cards.jsx — каркас виджета и скелетон сетки.
   WidgetCard — каркас виджета аналитики (loading/error/empty/content) + Excel-экспорт.
   SkeletonGrid — сетка skeleton-карточек загрузки (раздел «Продукция» до ответа режима).
   MiniBarChart — лёгкий SVG bar-chart (ЭКВИВАЛЕНТ recharts для прототипа).
   Плитки реестров (IngredientGridCard, RecipeCard, MenuGridCard, DocumentTile) удалены в
   kitchen-ux E1: реестры «Продукции» — таблицы слоя (components/patterns/table), карточка
   документа базы знаний — DocumentCard.jsx.
   ХЕНДОФФ → frontend/components/common/ (WidgetCard, SkeletonGrid). */

import React from 'react';
import { Icon } from '../ui/Icon.jsx';
import { Popover } from '../ui/Popover.jsx';
import { LoadingState, EmptyState, ErrorState, ForbiddenState } from './States.jsx';
import { useToast } from '../../app/providers/ToastProvider.jsx';

  const { useState } = React;

  /* ── MiniBarChart (recharts-эквивалент) ── */
  function MiniBarChart({ data = [], height = 140, color = 'var(--accent)' }) {
    const max = Math.max(1, ...data.map((d) => d.value));
    return (
      <div className="mini-chart" style={{ height }}>
        {data.map((d, i) => (
          <div className="mini-chart__col" key={i}>
            <div className="mini-chart__bar" style={{ height: (d.value / max * 100) + '%', background: color }}>
              <span className="mini-chart__tip num">{d.value}</span>
            </div>
            <span className="mini-chart__label t-caption truncate">{d.label}</span>
          </div>
        ))}
      </div>
    );
  }

  /* ── WidgetCard ──
     Состояния тела рендерятся каноническими State-компонентами (States.jsx),
     а не форк-хромом — единые иконки/размеры/тексты. `skeleton` (опц.) даёт
     per-тип skeleton тела на загрузке (как KPI-бар) вместо общего спиннера. */
  function WidgetCard({ title, subtitle, actions, onExport, loading, error, forbidden, empty, emptyLabel, onRetry, skeleton, children, className, help }) {
    const toast = useToast();
    const [exporting, setExporting] = useState(false);
    // Скачивание — async (серверный ?export=xlsx): пока качается — спиннер,
    // ошибка — тост, а не unhandled rejection (раньше кнопка молча «не работала»).
    const handleExport = async () => {
      if (!onExport || exporting) return;
      setExporting(true);
      try {
        await onExport();
      } catch {
        toast?.show('Не удалось скачать файл. Попробуйте ещё раз.', 'danger');
      } finally {
        setExporting(false);
      }
    };
    let body;
    if (loading) body = skeleton || <LoadingState />;
    else if (forbidden) body = <ForbiddenState body="Нет доступа к данным виджета." />;
    else if (error) body = <ErrorState title="Не удалось загрузить" body="Данные виджета недоступны. Попробуйте ещё раз." onRetry={onRetry} />;
    else if (empty) body = <EmptyState icon="analytics" serif={false} title={emptyLabel || 'Нет данных за период'} />;
    else body = children;
    return (
      <section className={'widget-card' + (className ? ' ' + className : '')}>
        <header className="widget-card__head">
          <div className="grow" style={{ minWidth: 0 }}>
            <h3 className="widget-card__title t-h4 truncate">{title}</h3>
            {subtitle && <div className="widget-card__sub t-caption truncate">{subtitle}</div>}
          </div>
          <div className="widget-card__actions cluster cluster-4">
            {actions}
            {help && (
              <Popover placement="bottom-end" width={280}
                trigger={(
                  <button type="button" className="icon-btn-mini" aria-label="Об отчёте" title="Об отчёте">
                    <Icon name="info" size={16} />
                  </button>
                )}>
                <div className="widget-help">{help}</div>
              </Popover>
            )}
            {onExport && (
              <button type="button" className="icon-btn-mini" onClick={handleExport} disabled={exporting} aria-label="Скачать Excel" title="Скачать Excel">
                <Icon name={exporting ? 'spinner' : 'download'} size={16} className={exporting ? 'icon--spin' : undefined} />
              </button>
            )}
          </div>
        </header>
        <div className="widget-card__body">{body}</div>
      </section>
    );
  }

  /* ── SkeletonGrid ── */
  function SkeletonGrid({ count = 6, aspect = '1.3', className }) {
    return (
      <div className={'tile-grid' + (className ? ' ' + className : '')}>
        {Array.from({ length: count }).map((_, i) => (
          <div className="tile tile--skeleton" key={i}>
            <div className="cluster cluster-4"><span className="skeleton" style={{ width: 64, height: 18, borderRadius: 999 }} /></div>
            <span className="skeleton" style={{ width: '80%', height: 18 }} />
            <span className="skeleton" style={{ width: '60%', height: 14 }} />
            <span className="skeleton" style={{ width: '40%', height: 14 }} />
          </div>
        ))}
      </div>
    );
  }

export { WidgetCard };
export { SkeletonGrid };
export { MiniBarChart };
